import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TicketPercent, Plus, Edit3, Trash2, X, Loader2, Search, Power,
  PowerOff, Coins, Dices, ShieldCheck, History, RefreshCw,
  Sparkles, SlidersHorizontal, CalendarClock, Repeat2, ShieldAlert, Save,
  Wallet,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';

const emptyForm = {
  code: '',
  reward_type: 'cash',
  reward_amount: 0,
  game_id: '',
  spin_count: 0,
  wager_multiplier: 0,
  min_deposit: 0,
  usage_limit: 0,
  allow_repeat: false,
  kyc_required: false,
  // Kodun kendi bakiye şartı: açıkken ana + bonus bakiyesi eşiğin üstündeki
  // üye kodu kullanamaz. Eşik 0 = bakiyesi tamamen bitmiş olmalı.
  block_positive_balance: false,
  max_balance: 0,
  starts_at: '',
  ends_at: '',
  active: true,
};

// Kod kullanım kapısı (backend: services/promo_rules.py). İki kural da
// varsayılan olarak kapalıdır; kayıt `provider_configs` satırındadır.
// `guest_view` ise sayfanın ziyaretçiye açık olup olmadığıdır ve varsayılanı
// açıktır (sayfa bugüne kadar ziyaretçiye açıktı).
const emptyRules = {
  block_active_wager: false,
  block_positive_balance: false,
  max_balance: 0,
  guest_view: true,
};

const money = (value) => new Intl.NumberFormat('tr-TR', {
  style: 'currency', currency: 'TRY', maximumFractionDigits: 2,
}).format(Number(value || 0));

const formatDate = (value) => (value ? new Date(value).toLocaleString('tr-TR') : 'Süresiz');
// datetime-local alanı yerel saat bekler; toISOString() ise UTC verir. Kaydetme
// yolu (new Date(form.starts_at).toISOString()) girilen değeri yerel saat kabul
// ettiği için, dolduruken de yerel saate çevirmezsek tarih her kayıtta UTC farkı
// kadar geri kayar.
const toInputDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const PromoCodes = () => {
  const [items, setItems] = useState([]);
  const [games, setGames] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [view, setView] = useState('codes');
  const [rules, setRules] = useState(emptyRules);
  const [rulesDraft, setRulesDraft] = useState(emptyRules);
  const [savingRules, setSavingRules] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  // Freespin, oyuncu bazlı tanımlanabilen sağlayıcılardan verilir: Gold Palace
  // (freeround) veya ScorpioPlay (bonus-call). Drakon buraya girmez.
  const FREESPIN_PROVIDERS = ['goldpalace', 'scorpio'];
  const freespinGames = useMemo(
    () => games.filter(
      (game) => FREESPIN_PROVIDERS.includes(game.api_provider) && game.active),
    [games],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [
        { data: codes }, { data: gameData }, { data: redemptionData },
        { data: ruleData },
      ] = await Promise.all([
        client.get('/admin/promo-codes'),
        // Oyun kataloğu yalnızca games.read yetkisiyle okunur; yetkisi olmayan
        // rollerde promosyon sayfası oyun listesi olmadan da çalışmalı.
        client.get('/admin/games').catch(() => ({ data: {} })),
        client.get('/admin/promo-codes/redemptions'),
        client.get('/admin/promo-codes/settings'),
      ]);
      setItems(codes.items || []);
      setGames(gameData.items || []);
      setRedemptions(redemptionData.items || []);
      const loadedRules = { ...emptyRules, ...(ruleData || {}) };
      setRules(loadedRules);
      setRulesDraft(loadedRules);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Promosyon kodları alınamadı');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleUpperCase('tr-TR');
    if (!needle) return items;
    return items.filter((item) => item.code.includes(needle));
  }, [items, query]);

  const openNew = () => {
    setForm(emptyForm);
    setModal({ mode: 'new' });
  };

  const openEdit = (item) => {
    setForm({
      ...emptyForm,
      ...item,
      game_id: item.game_id || '',
      starts_at: toInputDate(item.starts_at),
      ends_at: toInputDate(item.ends_at),
    });
    setModal({ mode: 'edit', id: item.id });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        reward_amount: Number(form.reward_amount || 0),
        spin_count: Number(form.spin_count || 0),
        wager_multiplier: Number(form.wager_multiplier || 0),
        min_deposit: Number(form.min_deposit || 0),
        usage_limit: Number(form.usage_limit || 0),
        block_positive_balance: !!form.block_positive_balance,
        max_balance: form.block_positive_balance ? Number(form.max_balance || 0) : 0,
        game_id: form.reward_type === 'freespin' ? form.game_id : null,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      };
      if (modal.mode === 'new') await client.post('/admin/promo-codes', payload);
      else await client.patch(`/admin/promo-codes/${modal.id}`, payload);
      toast({ title: 'Promosyon kodu kaydedildi' });
      setModal(null);
      await load();
    } catch (requestError) {
      toast({
        title: 'Kaydedilemedi',
        description: requestError.response?.data?.detail || 'Beklenmeyen bir hata oluştu',
      });
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (item) => {
    const previous = items;
    setItems((current) => current.map((code) => (
      code.id === item.id ? { ...code, active: !code.active } : code
    )));
    try {
      await client.patch(`/admin/promo-codes/${item.id}`, { active: !item.active });
    } catch (requestError) {
      setItems(previous);
      toast({ title: 'Durum güncellenemedi', description: requestError.response?.data?.detail });
    }
  };

  const archive = async (item) => {
    if (!window.confirm(`${item.code} kodu arşivlensin mi? Kullanım kayıtları korunacaktır.`)) return;
    try {
      await client.delete(`/admin/promo-codes/${item.id}`);
      setItems((current) => current.filter((code) => code.id !== item.id));
      toast({ title: 'Kod güvenle arşivlendi' });
    } catch (requestError) {
      toast({ title: 'Arşivlenemedi', description: requestError.response?.data?.detail });
    }
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      const { data } = await client.patch('/admin/promo-codes/settings', {
        block_active_wager: !!rulesDraft.block_active_wager,
        block_positive_balance: !!rulesDraft.block_positive_balance,
        max_balance: Number(rulesDraft.max_balance || 0),
        guest_view: rulesDraft.guest_view !== false,
      });
      const saved = { ...emptyRules, ...(data || {}) };
      setRules(saved);
      setRulesDraft(saved);
      toast({ title: 'Kod kullanım kuralları kaydedildi' });
    } catch (requestError) {
      toast({
        title: 'Kurallar kaydedilemedi',
        description: requestError.response?.data?.detail || 'Beklenmeyen bir hata oluştu',
      });
    } finally {
      setSavingRules(false);
    }
  };

  const rulesDirty = (
    !!rules.block_active_wager !== !!rulesDraft.block_active_wager
    || !!rules.block_positive_balance !== !!rulesDraft.block_positive_balance
    || Number(rules.max_balance || 0) !== Number(rulesDraft.max_balance || 0)
    || (rules.guest_view !== false) !== (rulesDraft.guest_view !== false)
  );

  return (
    <div>
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="w-10 h-10 rounded-xl grid place-items-center bg-violet-400/10 text-violet-300 border border-violet-400/20">
          <TicketPercent size={19} />
        </div>
        <div>
          <h1 className="text-xl font-black">Promosyon Kodları</h1>
          <p className="text-xs text-slate-400">Cüzdan ve oyun sağlayıcısına bağlı nakit/freespin kodları</p>
        </div>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={load} className="icon-btn view" title="Yenile"><RefreshCw size={14} /></button>
          <button type="button" onClick={openNew} className="admin-btn-primary flex items-center gap-2">
            <Plus size={14} /> Yeni Kod
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button type="button" onClick={() => setView('codes')} className={`admin-tab ${view === 'codes' ? 'active' : ''}`}>
          Kodlar ({items.length})
        </button>
        <button type="button" onClick={() => setView('redemptions')} className={`admin-tab ${view === 'redemptions' ? 'active' : ''}`}>
          <History size={13} className="inline mr-1" /> Kullanım Logları ({redemptions.length})
        </button>
        <button type="button" onClick={() => setView('rules')} className={`admin-tab ${view === 'rules' ? 'active' : ''}`}>
          <ShieldAlert size={13} className="inline mr-1" /> Kullanım Kuralları
          {(rules.block_active_wager || rules.block_positive_balance) && (
            <span className="ml-1 text-[10px] text-emerald-300">açık</span>
          )}
        </button>
        {view === 'codes' && (
          <div className="relative ml-auto w-full sm:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="admin-input pl-10"
              placeholder="Kod ara..."
            />
          </div>
        )}
      </div>

      {loading ? (
        <div className="admin-card py-20 text-center text-slate-400">
          <Loader2 className="inline animate-spin text-cyan-400 mr-2" /> Yükleniyor
        </div>
      ) : error ? (
        <div className="admin-card p-10 text-center text-red-300">{error}</div>
      ) : view === 'codes' ? (
        <div className="admin-card overflow-x-auto">
          <table className="admin-table w-full min-w-[1000px]">
            <thead>
              <tr>
                <th>Kod</th><th>Ödül</th><th>Şartlar</th><th>Kullanım</th>
                <th>Tarih</th><th>Durum</th><th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const percent = item.usage_limit > 0
                  ? Math.min(100, (item.used_count / item.usage_limit) * 100) : 0;
                const game = games.find((entry) => entry.id === item.game_id);
                return (
                  <tr key={item.id}>
                    <td>
                      <div className="font-mono font-black text-violet-300 text-base">{item.code}</div>
                      <div className="text-[11px] text-slate-500 mt-1">
                        {item.allow_repeat ? 'Tekrar kullanılabilir' : 'Kullanıcı başına tek kullanım'}
                      </div>
                    </td>
                    <td>
                      <span className={`badge-pill ${item.reward_type === 'cash' ? 'badge-green' : 'badge-blue'}`}>
                        {item.reward_type === 'cash' ? <Coins size={11} /> : <Dices size={11} />}
                        {item.reward_type === 'cash' ? money(item.reward_amount) : `${item.spin_count} Freespin`}
                      </span>
                      {game && <div className="text-[11px] text-slate-500 mt-1 max-w-40 truncate">{game.name}</div>}
                    </td>
                    <td className="text-xs text-slate-400">
                      <div>Min. yatırım: {money(item.min_deposit)}</div>
                      <div>Çevrim: x{Number(item.wager_multiplier || 0)}</div>
                      {item.kyc_required && <div className="text-amber-300 mt-1"><ShieldCheck size={11} className="inline" /> KYC gerekli</div>}
                      {item.block_positive_balance && (
                        <div className="text-amber-300 mt-1">
                          <Wallet size={11} className="inline" />{' '}
                          {Number(item.max_balance || 0) > 0
                            ? `Bakiye ${money(item.max_balance)} altında olmalı`
                            : 'Bakiyesi olan kullanamaz'}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="flex justify-between text-[11px] text-slate-400 mb-1">
                        <span>{item.used_count || 0}</span>
                        <span>{item.usage_limit > 0 ? item.usage_limit : '∞'}</span>
                      </div>
                      <div className="w-28 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                        <div className="h-full bg-violet-400" style={{ width: item.usage_limit > 0 ? `${percent}%` : '0%' }} />
                      </div>
                    </td>
                    <td className="text-xs text-slate-400">
                      <div>{formatDate(item.starts_at)}</div>
                      <div className="text-slate-600 mt-1">{formatDate(item.ends_at)}</div>
                    </td>
                    <td>
                      <span className={`badge-pill ${item.active ? 'badge-green' : 'badge-gray'}`}>
                        {item.active ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button type="button" onClick={() => openEdit(item)} className="icon-btn edit" title="Düzenle"><Edit3 size={13} /></button>
                        <button type="button" onClick={() => toggle(item)} className="icon-btn view" title={item.active ? 'Devre dışı bırak' : 'Etkinleştir'}>
                          {item.active ? <PowerOff size={13} /> : <Power size={13} />}
                        </button>
                        <button type="button" onClick={() => archive(item)} className="icon-btn ban" title="Arşivle"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-slate-500">Promosyon kodu bulunamadı</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : view === 'rules' ? (
        <div className="admin-card p-5 max-w-3xl">
          <h2 className="font-black text-[15px]">Kod Kullanım Kuralları</h2>
          <p className="text-[12px] text-slate-400 mt-1 mb-5">
            Kodun kendi şartlarından (KYC, minimum yatırım, tekrar hakkı) ayrıdır:
            bu iki kural <b>bütün kodlara</b> uygulanır ve aynı oyuncunun üst üste
            kod kullanmasını engeller. İkisi de varsayılan olarak kapalıdır.
          </p>

          <label className="flex items-start gap-3 p-3 rounded-xl border border-white/10 bg-white/[0.02] cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={!!rulesDraft.block_active_wager}
              onChange={(event) => setRulesDraft((current) => ({
                ...current, block_active_wager: event.target.checked,
              }))}
            />
            <span>
              <span className="font-bold text-[13px]">Aktif çevrimi olan üye kod kullanamasın</span>
              <span className="block text-[11px] text-slate-400 mt-1">
                Bonus veya kayıp bonusu çevrimi sürerken yeni kod reddedilir
                (kampanya bonuslarındaki istifleme korumasının aynısı). Çevrim
                tamamlanınca kapı kendiliğinden açılır.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 p-3 mt-3 rounded-xl border border-white/10 bg-white/[0.02] cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={!!rulesDraft.block_positive_balance}
              onChange={(event) => setRulesDraft((current) => ({
                ...current, block_positive_balance: event.target.checked,
              }))}
            />
            <span>
              <span className="font-bold text-[13px]">Bakiyesi olan üye kod kullanamasın</span>
              <span className="block text-[11px] text-slate-400 mt-1">
                Ölçü ana bakiye + bonus bakiyesidir. Eşiği <b>0</b> bırakmak
                &quot;bakiyesi tamamen bitmiş olmalı&quot; demektir; kuralı kapatmak
                için kutuyu boşaltın.
              </span>
            </span>
          </label>

          {rulesDraft.block_positive_balance && (
            <div className="mt-3 pl-3 border-l-2 border-violet-400/40">
              <label className="admin-label">Kod kullanılabilecek en yüksek bakiye (₺)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="admin-input admin-input-w-md"
                value={rulesDraft.max_balance}
                onChange={(event) => setRulesDraft((current) => ({
                  ...current, max_balance: event.target.value,
                }))}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Bakiyesi bu tutarın üstünde olan üye kodu kullanamaz; eşiğin
                kendisi geçerlidir.
              </p>
            </div>
          )}

          <h3 className="font-black text-[13px] mt-6">Sayfa Erişimi</h3>
          <label className="flex items-start gap-3 p-3 mt-2 rounded-xl border border-white/10 bg-white/[0.02] cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={rulesDraft.guest_view !== false}
              onChange={(event) => setRulesDraft((current) => ({
                ...current, guest_view: event.target.checked,
              }))}
            />
            <span>
              <span className="font-bold text-[13px]">Giriş yapmamış ziyaretçi Promosyon Kodu sayfasını görebilsin</span>
              <span className="block text-[11px] text-slate-400 mt-1">
                Varsayılan olarak açıktır. Kapatıldığında <code>/promosyon-kodu</code>{' '}
                adresine giriş yapmadan gelen ziyaretçi sayfa yerine &quot;Giriş
                Gerekli&quot; ekranını görür. Kodu kullanmak her iki durumda da üye
                girişi ister.
              </span>
            </span>
          </label>

          <div className="flex items-center gap-3 mt-5">
            <button
              type="button"
              onClick={saveRules}
              disabled={savingRules || !rulesDirty}
              className="admin-btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {savingRules ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Kuralları Kaydet
            </button>
            {rulesDirty && (
              <span className="text-[11px] text-amber-300">Kaydedilmemiş değişiklik var</span>
            )}
          </div>
        </div>
      ) : (
        <div className="admin-card overflow-x-auto">
          <table className="admin-table w-full min-w-[760px]">
            <thead><tr><th>Tarih</th><th>Kod</th><th>Kullanıcı</th><th>Ödül</th><th>Durum</th></tr></thead>
            <tbody>
              {redemptions.map((item) => (
                <tr key={item.id}>
                  <td className="text-slate-400">{formatDate(item.created_at)}</td>
                  <td className="font-mono font-bold text-violet-300">{item.code}</td>
                  <td className="font-semibold">{item.username}</td>
                  <td>{item.reward_type === 'cash' ? money(item.reward_amount) : 'Freespin'}</td>
                  <td><span className={`badge-pill ${item.status === 'completed' ? 'badge-green' : 'badge-yellow'}`}>{item.status}</span></td>
                </tr>
              ))}
              {redemptions.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12 text-slate-500">Henüz kullanım kaydı yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => !busy && setModal(null)}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          <div className="relative admin-card admin-modal w-full max-w-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="admin-modal-head">
              <div className="admin-modal-icon"><TicketPercent size={19} /></div>
              <div className="min-w-0">
                <h2 className="font-black text-[15px] leading-tight">{modal.mode === 'new' ? 'Yeni Promosyon Kodu' : 'Promosyon Kodunu Düzenle'}</h2>
                <p className="text-[11px] text-slate-500 mt-0.5">Ödül, uygunluk ve zamanlama kurallarını tanımlayın</p>
              </div>
              <button type="button" onClick={() => setModal(null)} className="icon-btn view ml-auto"><X size={14} /></button>
            </div>

            <div className="admin-modal-body">
              <section className="admin-fieldset">
                <h3 className="admin-fieldset-title"><Sparkles size={12} /> Kod ve Ödül</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Field label="Kod" hint="Oyuncunun Promosyonlar sayfasına gireceği metin. Büyük harfe çevrilir.">
                      <input value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })} className="admin-input font-mono font-bold uppercase tracking-[0.16em]" placeholder="YAZ2026" />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-xs text-slate-400 block mb-1.5">Ödül Türü</span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <RewardChoice
                        icon={Coins}
                        title="Nakit"
                        description="Ana bakiyeye yazılır"
                        active={form.reward_type === 'cash'}
                        onSelect={() => setForm({ ...form, reward_type: 'cash' })}
                      />
                      <RewardChoice
                        icon={Dices}
                        title="Freespin"
                        description="Oyunun sağlayıcısında tanımlanır"
                        active={form.reward_type === 'freespin'}
                        onSelect={() => setForm({ ...form, reward_type: 'freespin' })}
                      />
                    </div>
                  </div>
                  <Field
                    label={form.reward_type === 'cash' ? 'Ödül Tutarı' : 'Spin Bahis Tutarı (isteğe bağlı)'}
                    hint={form.reward_type === 'freespin'
                      ? '0 bırakırsanız Freespin Kontrol sayfasındaki varsayılan bahis kullanılır; kullanıcının bakiyesinden para düşmez.'
                      : undefined}
                  >
                    <Affix text="₺">
                      <input type="number" min="0" step="0.01" value={form.reward_amount} onChange={(event) => setForm({ ...form, reward_amount: event.target.value })} className="admin-input pr-10" />
                    </Affix>
                  </Field>
                  {form.reward_type === 'freespin' && (
                    <>
                      <Field label="Spin Sayısı">
                        <Affix text="adet">
                          <input type="number" min="1" value={form.spin_count} onChange={(event) => setForm({ ...form, spin_count: event.target.value })} className="admin-input pr-10" />
                        </Affix>
                      </Field>
                      <div className="sm:col-span-2">
                        <Field label="İlişkili Oyun" hint={freespinGames.length > 0 ? 'Hak bu oyunun sağlayıcısında açılır; oyuncu spinleri o oyunda kullanır.' : undefined}>
                          {freespinGames.length > 0 ? (
                            <select value={form.game_id} onChange={(event) => setForm({ ...form, game_id: event.target.value })} className="admin-input">
                              <option value="">Oyun seçin</option>
                              {freespinGames.map((game) => (
                                <option key={game.id} value={game.id}>
                                  {game.name} · {game.provider} ({game.reel_provider_name || game.api_provider})
                                </option>
                              ))}
                            </select>
                          ) : (
                            // Boş liste yerine nedenini yazıyoruz: freespin Gold Palace
                            // (freeround) veya ScorpioPlay (bonus-call) ile verilir.
                            <p className="text-[12px] leading-relaxed rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-amber-100">
                              Freespin ödülü Gold Palace veya ScorpioPlay entegrasyonunu gerektirir;
                              şu anda aktif bir freespin oyunu yok. Bu servislerden birini Oyun API
                              ayarlarından yapılandırıp açmadan freespin promosyonu oluşturulamaz.
                            </p>
                          )}
                        </Field>
                      </div>
                    </>
                  )}
                </div>
              </section>

              <section className="admin-fieldset">
                <h3 className="admin-fieldset-title"><SlidersHorizontal size={12} /> Uygunluk Kuralları</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label="Çevrim Şartı" hint="0 = çevrimsiz">
                    <Affix text="×" side="left">
                      <input type="number" min="0" step="0.1" value={form.wager_multiplier} onChange={(event) => setForm({ ...form, wager_multiplier: event.target.value })} className="admin-input pl-10" />
                    </Affix>
                  </Field>
                  <Field label="Minimum Yatırım" hint="0 = şart yok">
                    <Affix text="₺">
                      <input type="number" min="0" step="0.01" value={form.min_deposit} onChange={(event) => setForm({ ...form, min_deposit: event.target.value })} className="admin-input pr-10" />
                    </Affix>
                  </Field>
                  <Field label="Kullanım Limiti" hint="0 = sınırsız">
                    <Affix text="kez">
                      <input type="number" min="0" value={form.usage_limit} onChange={(event) => setForm({ ...form, usage_limit: event.target.value })} className="admin-input pr-10" />
                    </Affix>
                  </Field>
                </div>
              </section>

              <section className="admin-fieldset">
                <h3 className="admin-fieldset-title"><CalendarClock size={12} /> Zamanlama</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Başlangıç" hint="Boş bırakılırsa hemen başlar">
                    <input type="datetime-local" value={form.starts_at} onChange={(event) => setForm({ ...form, starts_at: event.target.value })} className="admin-input" />
                  </Field>
                  <Field label="Bitiş" hint="Boş bırakılırsa süresizdir">
                    <input type="datetime-local" value={form.ends_at} onChange={(event) => setForm({ ...form, ends_at: event.target.value })} className="admin-input" />
                  </Field>
                </div>
              </section>

              <section className="admin-fieldset">
                <h3 className="admin-fieldset-title"><ShieldCheck size={12} /> Durum ve Kısıtlar</h3>
                <div className="grid gap-2.5">
                  <SwitchRow
                    icon={Power}
                    label="Aktif"
                    description="Kapalıyken kod oyuncu tarafından kullanılamaz."
                    checked={!!form.active}
                    onChange={(active) => setForm({ ...form, active })}
                  />
                  <SwitchRow
                    icon={Repeat2}
                    label="Tekrar Kullanım"
                    description="Açıkken aynı üye kodu birden çok kez kullanabilir."
                    checked={!!form.allow_repeat}
                    onChange={(allow_repeat) => setForm({ ...form, allow_repeat })}
                  />
                  <SwitchRow
                    icon={ShieldCheck}
                    label="KYC Gerekli"
                    description="Yalnızca kimliği doğrulanmış üyeler kodu kullanabilir."
                    checked={!!form.kyc_required}
                    onChange={(kyc_required) => setForm({ ...form, kyc_required })}
                  />
                  <SwitchRow
                    icon={Wallet}
                    label="Bakiyesi Olan Kullanamaz"
                    description="Açıkken ana + bonus bakiyesi eşiğin üstündeki üye bu kodu kullanamaz."
                    checked={!!form.block_positive_balance}
                    onChange={(block_positive_balance) => setForm({ ...form, block_positive_balance })}
                  />
                  {form.block_positive_balance && (
                    <Field label="İzin Verilen En Yüksek Bakiye" hint="0 = bakiyesi tamamen bitmiş olmalı">
                      <Affix text="₺">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          aria-label="İzin verilen en yüksek bakiye"
                          value={form.max_balance}
                          onChange={(event) => setForm({ ...form, max_balance: event.target.value })}
                          className="admin-input pr-10"
                        />
                      </Affix>
                    </Field>
                  )}
                </div>
              </section>
            </div>

            <div className="admin-modal-foot">
              <span className="admin-modal-summary">
                <TicketPercent size={12} />
                <b className="font-mono truncate">{form.code.trim() || 'KOD'}</b>
                <span className="opacity-50">→</span>
                {form.reward_type === 'cash'
                  ? money(form.reward_amount)
                  : `${Number(form.spin_count) || 0} freespin`}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button type="button" onClick={() => setModal(null)} disabled={busy} className="admin-btn-secondary">Vazgeç</button>
                <button type="button" disabled={busy || !form.code.trim()} onClick={save} className="admin-btn-primary">
                  {busy && <Loader2 size={14} className="animate-spin" />} Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-xs text-slate-400 block mb-1.5">{label}</span>
    {children}
    {hint && <span className="mt-1.5 block text-[11px] leading-relaxed text-slate-500">{hint}</span>}
  </label>
);

// Girişin içinde duran birim etiketi (₺, ×, adet): alanın ne beklediğini
// etiketi uzatmadan söyler. Dolgu .admin-input.pl-10 / .pr-10 ile verilir —
// Tailwind'in padding utility'leri .admin-input tarafından eziliyor.
const Affix = ({ text, side = 'right', children }) => (
  <span className={`admin-affix block ${side === 'left' ? 'affix-left' : ''}`}>
    {children}
    <span className="admin-affix-text">{text}</span>
  </span>
);

const RewardChoice = ({ icon: Icon, title, description, active, onSelect }) => (
  <button type="button" onClick={onSelect} className={`admin-choice ${active ? 'active' : ''}`}>
    <span className="admin-choice-icon"><Icon size={16} /></span>
    <span className="min-w-0">
      <span className="block text-sm font-bold">{title}</span>
      <span className="block text-[11px] text-slate-500 mt-0.5">{description}</span>
    </span>
  </button>
);

const SwitchRow = ({ icon: Icon, label, description, checked, onChange }) => (
  <label className={`admin-switch ${checked ? 'on' : ''}`}>
    <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    <span className="admin-switch-track" aria-hidden="true" />
    <span className="min-w-0">
      <span className="flex items-center gap-1.5 text-sm font-semibold"><Icon size={13} /> {label}</span>
      <span className="block text-[11px] leading-relaxed text-slate-500 mt-0.5">{description}</span>
    </span>
  </label>
);

export default PromoCodes;
